import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { type Nullable } from '@web-config-app/core';
import { getQueryParamsFromUrl } from '../../utilities/get-query-params-from-url/get-query-params-from-url.util';

/**
 * Default pagination parameters for all entities
 */
export const initialPaginationParams = {
  'page[offset]': '0',
  'page[size]': '30',
};

export interface EntityListParams {
  /** Current page offset */
  offset: string;
  /** Page size */
  size: string;
  /** Current search query */
  searchQuery: Nullable<string>;
  /** Current status filter from URL */
  statusFilter: Nullable<string>;
  /** Load a specific page using a pagination URL */
  loadPage: (url: string | undefined) => void;
  /** Set search term and reset pagination */
  onSearch: (search?: string) => void;
  /** Set status filter and reset pagination */
  onStatusFilterChange: (status?: string) => void;
}

/**
 * Manages search and pagination parameters for entity lists.
 * It reads parameters from the URL and provides functions to update them.
 */
export function useEntityListParams(): EntityListParams {
  const location = useLocation();
  const navigate = useNavigate();

  const getParamFromUrl = React.useCallback(
    (param: string): string | null => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get(param);
    },
    [location.search],
  );

  // Read pagination and search parameters from URL
  const urlOffset =
    getParamFromUrl('offset') || initialPaginationParams['page[offset]'];
  const urlSize =
    getParamFromUrl('size') || initialPaginationParams['page[size]'];
  const searchQuery = getParamFromUrl('search');
  const statusFilter = getParamFromUrl('status');

  // Update URL with new search params
  const updateUrlParams = React.useCallback(
    (params: Record<string, string | undefined>) => {
      const currentParams = new URLSearchParams(location.search);

      // Update or remove each parameter
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
          currentParams.delete(key);
        } else {
          currentParams.set(key, value);
        }
      });

      const newPath = `${location.pathname}?${currentParams.toString()}`;

      navigate(newPath, {
        state: {
          entityListSearchParams: currentParams.toString(),
        },
      });
    },
    [location, navigate],
  );

  const loadPage = React.useCallback(
    (url: string | undefined) => {
      if (!url) return;
      const params = getQueryParamsFromUrl(url);

      // Extract offset and size from params
      const offset = params['page[offset]'] || '0';
      const size = params['page[size]'] || '30';

      // Update URL query params
      updateUrlParams({
        offset,
        size,
      });
    },
    [updateUrlParams],
  );

  const onSearch = React.useCallback(
    (search?: string) => {
      // Update URL with search term and reset pagination
      updateUrlParams({
        search: search || undefined, // Use undefined to remove the param instead of empty string
        // setting these to undefined will remove them from the URL
        offset: undefined,
        size: undefined,
      });
    },
    [updateUrlParams],
  );

  const onStatusFilterChange = React.useCallback(
    (status?: string) => {
      updateUrlParams({
        status: status || undefined,
        offset: undefined,
        size: undefined,
      });
    },
    [updateUrlParams],
  );

  return {
    offset: urlOffset,
    size: urlSize,
    searchQuery,
    statusFilter,
    loadPage,
    onSearch,
    onStatusFilterChange,
  };
}
